/**
 * Design tokens, carried over from the original standalone HTML design of the
 * dashboard (August 2026; the file is no longer kept).
 *
 * Colours live here rather than coming from the API on purpose: a badge tint is
 * a design decision, and changing one should not need a backend deploy. The API
 * owns the workflow (which statuses exist, which stage each maps to); this file
 * owns what those look like.
 */

export const color = {
  navy: '#12263f',
  ink: '#1c2c3e',
  /**
   * The quiet text colour, and the lightest one there is.
   *
   * There used to be a lighter `faint` (#9aa8b6) under this for labels, blank
   * placeholders and counts. It measured 2.43:1 on white and 2.05:1 on tint2,
   * against the 4.5:1 WCAG 2.1 AA asks for text — and every use of it was
   * text at 10.5–13.5px, which is "normal text" whatever its weight.
   *
   * It was not replaced with a darker light grey, because there is no room for
   * one: the lightest in-hue colour that passes on all four surfaces is
   * #636c74, which is this colour to the eye. So de-emphasis here is carried
   * by size, weight and uppercase rather than by a paler grey, and there is
   * one text grey instead of two. scripts/qa-contrast.mjs keeps it that way.
   */
  muted: '#5b6b7c',
  line: '#e2e9f1',
  bg: '#f4f7fb',
  tint: '#eef4fa',
  tint2: '#e3edf6',
  blue: '#33628e',
  blueDark: '#2a547e',
  blueDeep: '#1f4267',
  white: '#ffffff',
  green: '#14684c',
  red: '#a4432f',
} as const;

export const font = {
  sans: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
} as const;

/** Badge palette per shared stage. Keys match the API's stage values. */
export const stageTheme = {
  new: { bg: '#d9e7f5', fg: '#1f4267' },
  active: { bg: '#f6ddc2', fg: '#8a4f16' },
  done: { bg: '#cfe9d8', fg: '#14563c' },
  closed: { bg: '#e6eaef', fg: '#5b6b7c' },
} as const;

/** Big filter tiles above the inquiries table — one per stage, in this order. */
export const stageCardTheme = {
  new: { bg: '#c8ddf2', fg: '#1f4267' },
  active: { bg: '#f3cfa5', fg: '#7a4512' },
  done: { bg: '#f0e2a8', fg: '#6e5a12' },
  closed: { bg: '#b9e3c8', fg: '#14563c' },
} as const;

/** Form pill and donut colours. Keys match the API's form keys. */
export const formTheme = {
  intake: { bg: '#eef4fa', fg: '#2a547e', donut: '#33628e' },
  referral: { bg: '#e3edf6', fg: '#1f4267', donut: '#7ea8cf' },
  waitlist: { bg: '#e6f2ef', fg: '#1e6b5a', donut: '#58a884' },
  inquiry: { bg: '#f4f0e6', fg: '#7a6234', donut: '#e8a256' },
  // The registry forms (site push bd1db6d). Participant-path early interest
  // sits with the waitlist greens; the clinician trio shares one blue, the
  // self-service forms one grey, and the conversations one tone each.
  'early-interest': { bg: '#e6f2ef', fg: '#1e6b5a', donut: '#7fbfa0' },
  partnership: { bg: '#f1ecf6', fg: '#5b3f7a', donut: '#8e6db3' },
  media: { bg: '#f6ecec', fg: '#7a3f3f', donut: '#c07d7d' },
  fellowship: { bg: '#eaf1f1', fg: '#2f6363', donut: '#5f9c9c' },
  'pre-referral-fit': { bg: '#e3edf6', fg: '#1f4267', donut: '#5c8cb8' },
  'clinician-alerts': { bg: '#e3edf6', fg: '#1f4267', donut: '#9db9d4' },
  'referral-status': { bg: '#e3edf6', fg: '#1f4267', donut: '#b7cde0' },
  'stop-contacting-me': { bg: '#efeff1', fg: '#4a4f5a', donut: '#8d93a1' },
  'how-to-reach-me': { bg: '#efeff1', fg: '#4a4f5a', donut: '#b3b8c4' },
  // A3 joins the self-service greys; E1 and E2 join fellowship's teal, since
  // all three are one education conversation to the team that answers them.
  'access-interpreter': { bg: '#efeff1', fg: '#4a4f5a', donut: '#6f7582' },
  'resident-rotation': { bg: '#eaf1f1', fg: '#2f6363', donut: '#7fb3b3' },
  'student-observer': { bg: '#eaf1f1', fg: '#2f6363', donut: '#a1c9c9' },
} as const;

export type StageKey = keyof typeof stageTheme;
export type FormKey = keyof typeof formTheme;

/**
 * The participant pipeline, in board order.
 *
 * These are not API values — the API stores a per-form status. `pipeline.ts`
 * projects those statuses onto these five columns, so the board reads as one
 * flow even though intake, referral and waitlist each have their own workflow.
 */
export const pipeline = [
  { key: 'intake', label: 'Intake / Referral', dot: '#33628e' },
  { key: 'assessment', label: 'Assessment', dot: '#e8a256' },
  { key: 'monitoring', label: 'Monitoring', dot: '#58a884' },
  { key: 'waitlist', label: 'Waiting list', dot: '#9aa8b6' },
  { key: 'failed', label: 'Screen failed', dot: '#d97b6c' },
] as const;

export type PipelineKey = (typeof pipeline)[number]['key'];

/**
 * Safety-flag severities (02.12). Their own foreground and background, like
 * the other badge palettes, and here rather than in the component so
 * scripts/qa-contrast.mjs checks them: "urgent today" used to be #a4432f on
 * this peach, which measured 4.31:1 — under the 4.5:1 WCAG 2.1 AA asks for
 * text this size. Darkening the text keeps the three severities reading as an
 * escalation, which lightening the background would have flattened.
 */
export const severityTheme = {
  watch: { bg: '#f6ddc2', fg: '#8a4f16' },
  'urgent-today': { bg: '#f6d0c4', fg: '#953a26' },
  'possible-sae': { bg: '#f3c0c0', fg: '#8c1f1f' },
} as const;

/** Study lifecycle badges, from the design's `studyStatusMeta`. */
/**
 * The exact chip labels from 00_ENACT_Source_Synthesis §3 — the pack lists them
 * as "Status chips (exact labels)", so they are quoted rather than paraphrased.
 * Colours follow the pack's guidance too: green for recruiting, slate for
 * enrolled, amber for in development, muted for completed.
 */
export const studyStatusTheme = {
  recruiting: { label: 'Actively Recruiting', bg: '#cfe9d8', fg: '#14563c' },
  active: { label: 'Enrollment Complete — Active', bg: '#d9e7f5', fg: '#1f4267' },
  development: { label: 'In Development', bg: '#f6ddc2', fg: '#8a4f16' },
  completed: { label: 'Completed', bg: '#e6eaef', fg: '#5b6b7c' },
  // 055: teal, as the site's Research & Trials page draws it.
  invitation: { label: 'Enrolling by invitation', bg: '#d3e9ec', fg: '#1d5d67' },
} as const;

export type StudyStatus = keyof typeof studyStatusTheme;

/**
 * Whether a study is on the public site. One pair for the Studies table and
 * the sidebar's study list, so "Draft" looks the same wherever it is said:
 * published borrows the recruiting green, draft the completed grey.
 */
export const publishTheme = {
  published: { label: 'Published', bg: '#cfe9d8', fg: '#14563c' },
  draft: { label: 'Draft', bg: '#e6eaef', fg: '#5b6b7c' },
} as const;

/**
 * Role pills on the Roles and Users screens.
 *
 * The values are the palette's own tokens written out (navy on white, tint
 * and tint2 under blueDeep, line under ink) rather than new colours, so every
 * pair here is one the cross-product check above already passes — and being
 * a bg/fg block, scripts/qa-contrast.mjs measures it again anyway.
 */
export const roleTheme = {
  owner: { bg: '#12263f', fg: '#ffffff' },
  preset: { bg: '#eef4fa', fg: '#1f4267' },
  named: { bg: '#e3edf6', fg: '#1f4267' },
  custom: { bg: '#e2e9f1', fg: '#1c2c3e' },
} as const;

export type RoleTone = keyof typeof roleTheme;

/**
 * What an incomplete REDCap instrument sets off, as a pill. Each pair is one
 * the palette already uses — the urgent safety badge for a chase, the amber
 * "watch" for held, the preview amber for a highlight, closed grey for none —
 * so the rule reads with the same weight it has elsewhere.
 */
export const redcapRuleTheme = {
  chase: { bg: '#f6d0c4', fg: '#953a26' },
  held: { bg: '#f6ddc2', fg: '#8a4f16' },
  tint: { bg: '#f4f0e6', fg: '#7a6234' },
  none: { bg: '#e6eaef', fg: '#5b6b7c' },
} as const;

/**
 * What a week's flag is, as a pill: urgent borrows the urgent safety badge,
 * watch the amber "watch", info the open-chase blue — pairs the palette
 * already passes, written out so scripts/qa-contrast.mjs measures them here.
 */
export const redcapFlagTheme = {
  urgent: { bg: '#f6d0c4', fg: '#953a26' },
  watch: { bg: '#f6ddc2', fg: '#8a4f16' },
  info: { bg: '#d9e7f5', fg: '#1f4267' },
} as const;

export type RedcapFlagSeverity = keyof typeof redcapFlagTheme;

/** A REDCap chase's state. Overdue borrows the chase pill: it is the same alarm, later. */
export const chaseTheme = {
  open: { bg: '#d9e7f5', fg: '#1f4267' },
  overdue: { bg: '#f6d0c4', fg: '#953a26' },
  done: { bg: '#cfe9d8', fg: '#14563c' },
  resolved: { bg: '#cfe9d8', fg: '#14563c' },
} as const;

/**
 * A board card's REDCap tag: this sign-up went out in an export file. The
 * open-chase blue, since it is information rather than an alarm.
 */
export const redcapBoardTheme = {
  exported: { bg: '#d9e7f5', fg: '#1f4267' },
} as const;

/** The amber "Preview · not wired to the API yet" pill, the inquiry form's own pair. */
export const previewTheme = {
  pill: { bg: '#f4f0e6', fg: '#7a6234' },
} as const;

/** Donut slice colours for the inquiries-by-topic chart, cycled in meta order. */
export const topicColors = ['#33628e', '#e8a256', '#58a884', '#7ea8cf', '#b58bc0', '#d97b6c'];

/**
 * A meeting's phase, as a pill: Preparing borrows the "new" blue, In meeting
 * the "active" amber, Done the "done" green — the stage palette's own pairs,
 * so the three read with the weight they have on the boards.
 */
export const meetingPhaseTheme = {
  preparing: { bg: '#d9e7f5', fg: '#1f4267' },
  in_meeting: { bg: '#f6ddc2', fg: '#8a4f16' },
  done: { bg: '#cfe9d8', fg: '#14563c' },
} as const;

/**
 * What a line in a meeting is. Quiet pairs the palette already passes — a
 * kind is a label to scan by, not an alarm; only late work is ever filled.
 */
export const itemKindTheme = {
  agenda: { bg: '#eef4fa', fg: '#1f4267' },
  note: { bg: '#e6eaef', fg: '#5b6b7c' },
  question: { bg: '#f4f0e6', fg: '#7a6234' },
  takeaway: { bg: '#cfe9d8', fg: '#14563c' },
  action: { bg: '#f6ddc2', fg: '#8a4f16' },
} as const;

/**
 * A ticket's priority as a pill: critical red, normal blue, low yellow — the
 * three colours the lab asked for (14 Oct 2026), so the pill says how urgent
 * at a glance. The red is its own, not the salmon of the urgent safety badge
 * nor the Overdue pill's; the yellow is a true yellow, apart from the orange
 * amber that means "due today" and "needs an owner". The dot on the New
 * ticket radios and the page's priority select uses `fg` alone.
 */
export const priorityTheme = {
  critical: { bg: '#f9d3d3', fg: '#8f1d1d' },
  normal: { bg: '#d9e7f5', fg: '#1f4267' },
  low: { bg: '#fbe79a', fg: '#6b5300' },
} as const;

/**
 * A ticket's status as a pill, beside the priority in the page header. Open
 * is the quiet tint so the priority carries the colour; done is the green
 * every other "finished" wears; dropped the closed grey.
 */
export const workStatusTheme = {
  open: { bg: '#eef4fa', fg: '#1f4267' },
  done: { bg: '#cfe9d8', fg: '#14563c' },
  dropped: { bg: '#e6eaef', fg: '#5b6b7c' },
} as const;
